import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Crown, 
  ArrowRight, 
  Gift, 
  Package, 
  Tag,
  Sparkles,
  Clock,
  Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuthContext } from '../lib/AuthProvider';
import { useRushdMembership } from '../context/RushdMembershipContext';
import { useCurrency } from '../context/CurrencyContext';
import { RUSHD_PLAN_CONFIG, RUSHD_PERKS } from '../types/rushd';
import { trackRushdPurchaseConversion } from '../services/visitorTrackingService';
import confetti from 'canvas-confetti';

const RushdSuccess = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuthContext();
  const { refreshMembership } = useRushdMembership();
  const { formatDisplayAmount, getConvertedItemPrice, selectedCurrency } = useCurrency();
  const [isRefreshing, setIsRefreshing] = useState(true);
  
  const plan = searchParams.get('plan') as 'monthly' | 'yearly' | null;
  const planConfig = plan ? RUSHD_PLAN_CONFIG[plan] : RUSHD_PLAN_CONFIG.monthly;
  
  // Refresh membership status when component mounts to get latest data
  useEffect(() => {
    const refreshWithDelay = async () => {
      // Small delay to allow Stripe webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        await refreshMembership();
        
        // Track purchase completion
        if (user?.uid && plan) {
          const amount = planConfig.price;
          await trackRushdPurchaseConversion(user.uid, plan, amount);
        }
      } catch (error) {
        console.error('Error refreshing membership:', error);
      } finally {
        setIsRefreshing(false);
      }
    };
    
    refreshWithDelay();
  }, [refreshMembership, user, plan, planConfig.price]);
  
  // Trigger confetti celebration when component mounts
  useEffect(() => {
    const triggerCelebration = () => {
      // Initial confetti burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00742F', '#FFC901', '#FFE55C', '#4ADE80', '#22C55E']
      });
      
      // Second burst with slight delay for more dramatic effect
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 90,
          origin: { y: 0.5 },
          colors: ['#00742F', '#FFC901', '#FFE55C', '#4ADE80', '#22C55E']
        });
      }, 300);
    };
    
    // Small delay to ensure page has loaded
    const timer = setTimeout(triggerCelebration, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Welcome to Rush'd! | JamPackd</title>
        <meta name="description" content="Welcome to JamPack'd Rush'd! Your premium membership is now active." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-full animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-jamaica-yellow/25 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-gradient-to-br from-jamaica-yellow/15 to-jamaica-green/15 rounded-full animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-jamaica-green/20 rounded-full animate-pulse delay-900"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
              <div className="relative">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                  <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-green rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] sm:text-xs font-bold text-white">✨</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-xs sm:text-sm font-bold text-jamaica-black">Premium Membership</span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-2">
                JamPack'd Rush'd
              </span>!
            </h1>
            
            <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              🎉 Your{' '}
              <span className="text-jamaica-yellow font-semibold">{planConfig.name.toLowerCase()} membership</span>{' '}
              is now active. Get ready to enjoy{' '}
              <span className="text-jamaica-green font-semibold">premium benefits</span>{' '}
              on every order!
            </p>
            <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              🎉 Your {planConfig.name.toLowerCase()} membership is now active. Get ready to enjoy premium benefits on every order!
            </p>
          </div>
          
          
          {/* Benefits Overview with Plan Details */}
          <Card className="mb-8 bg-gradient-to-br from-white via-jamaica-green/5 to-jamaica-yellow/5 border-jamaica-green/20">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-jamaica-green" />
                  Your Rush'd Benefits
                </CardTitle>
                
                {/* Plan Badge */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-jamaica-green text-white px-3 py-1.5 text-sm font-semibold">
                    {planConfig.name}
                  </Badge>
                  {plan === 'yearly' && (
                    <Badge variant="outline" className="border-jamaica-yellow text-jamaica-yellow border-2 px-3 py-1.5 text-sm font-semibold">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Best Value
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Plan Details */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-3 pt-3 border-t border-jamaica-green/10">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-semibold text-gray-900">
                    {formatDisplayAmount(getConvertedItemPrice(planConfig.price))}/{planConfig.interval}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm ml-auto">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-600 font-medium">Active Now</span>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {RUSHD_PERKS.map((perk, index) => {
                  const IconComponent = {
                    Package,
                    Tag,
                    Gift,
                  }[perk.icon] || Package;
                  
                  return (
                    <div key={perk.id} className="text-center p-4 bg-white/70 rounded-xl border border-white/50 hover:bg-white/90 transition-colors duration-200">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-jamaica-green/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-jamaica-green" />
                      </div>
                      <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">{perk.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-600">{perk.description}</p>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 rounded-lg border border-jamaica-green/20">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-jamaica-green/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-3 h-3 text-jamaica-green" />
                  </div>
                  <div>
                    <h4 className="font-medium text-jamaica-green mb-1 text-sm sm:text-base">Active Immediately</h4>
                    <p className="text-xs sm:text-sm text-jamaica-green/80">
                      All benefits are now active on your account. Your next Express shipping order will automatically use member pricing!
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <Package className="w-8 h-8 text-jamaica-green mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Start Shopping</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Browse authentic Jamaican products and use your member benefits on Express shipping.
                  </p>
                  <Button asChild className="w-full bg-jamaica-green hover:bg-jamaica-green/90">
                    <Link to="/products">
                      Shop Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <Crown className="w-8 h-8 text-jamaica-green mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Manage Membership</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    View your membership details, usage, and manage billing settings.
                  </p>
                  <Button asChild variant="outline" className="w-full border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5">
                    <Link to="/settings?tab=rushd">
                      View Settings
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* What's Next */}
          <Card className="bg-gradient-to-r from-gray-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                What's Next?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-jamaica-green rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Add Items to Cart</h4>
                    <p className="text-sm text-gray-600">Browse our collection of authentic Jamaican products and add your favourites to the cart.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-jamaica-green rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Select Express Shipping</h4>
                    <p className="text-sm text-gray-600">Choose Express shipping at checkout to automatically apply your member discount.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-jamaica-green rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Enjoy the Savings</h4>
                    <p className="text-sm text-gray-600">See your member pricing applied and enjoy priority processing with potential surprise treats!</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Footer Message */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Questions about your membership? Check out our 
              <Link to="/faq" className="text-jamaica-green hover:underline mx-1">FAQ</Link>
              or contact our support team.
            </p>
            <p className="text-sm text-gray-500">
              Welcome to the Rush'd family! 🇯🇲
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RushdSuccess;